import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Star, Tractor, HelpCircle, XCircle, Brain, Loader2, Download, FileSpreadsheet, FileText, Share2, MessageCircle, Mail } from 'lucide-react';

const CATEGORY_CONFIG = {
  star: { icon: Star, label: 'Star', shortLabel: 'STAR', color: 'text-gold', bg: 'bg-gold/10 border-gold/30', desc: 'Alta popularidad + Alta rentabilidad' },
  plowhorse: { icon: Tractor, label: 'Plowhorse', shortLabel: 'PLOW', color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/30', desc: 'Alta popularidad + Baja rentabilidad' },
  puzzle: { icon: HelpCircle, label: 'Puzzle', shortLabel: 'PUZZLE', color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/30', desc: 'Baja popularidad + Alta rentabilidad' },
  dog: { icon: XCircle, label: 'Dog', shortLabel: 'DOG', color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/30', desc: 'Baja popularidad + Baja rentabilidad' },
};

export default function MenuEngineering() {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [shareMenu, setShareMenu] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axiosAuth().get('/menu-engineering');
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [axiosAuth]);

  const runAI = async () => {
    setAiLoading(true);
    try {
      const res = await axiosAuth().post('/menu-engineering/ai-analyze');
      setAiData(res.data);
    } catch (e) { console.error(e); alert('Error en analisis IA'); }
    finally { setAiLoading(false); }
  };

  const exportReport = async (type) => {
    setExporting(type);
    try {
      const url = type === 'excel' ? '/reports/menu-engineering/excel' : '/reports/menu-engineering/pdf';
      const res = await axiosAuth().get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `M3_MenuEngineering.${type === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
    } catch (e) {
      console.error(e);
      alert('Error al exportar');
    } finally {
      setExporting(null);
    }
  };

  const shareVia = (type) => {
    const appUrl = window.location.origin;
    const text = `M3 | Reporte Menu Engineering - Analisis de rentabilidad de menu`;
    if (type === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n\nVer en: ' + appUrl + '/menu-engineering')}`, '_blank');
    } else if (type === 'email') {
      window.location.href = `mailto:?subject=${encodeURIComponent('M3 Menu Engineering Report')}&body=${encodeURIComponent(text + '\n\nVer reporte completo en: ' + appUrl + '/menu-engineering')}`;
    }
    setShareMenu(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  const classifications = data?.classifications || [];
  const groups = { star: [], plowhorse: [], puzzle: [], dog: [] };
  classifications.forEach(c => { if (groups[c.category]) groups[c.category].push(c); });

  return (
    <div className="space-y-8 animate-fade-in" data-testid="menu-engineering-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Menu Engineering</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Matriz BCG</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Export Buttons */}
          <button onClick={() => exportReport('excel')} disabled={exporting === 'excel'} data-testid="export-excel-btn" 
            className="flex items-center gap-2 px-3 py-2 border border-emerald-400/40 text-emerald-400 text-xs font-bold uppercase tracking-widest hover:bg-emerald-400/10 transition-colors disabled:opacity-50">
            {exporting === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
            Excel
          </button>
          <button onClick={() => exportReport('pdf')} disabled={exporting === 'pdf'} data-testid="export-pdf-btn" 
            className="flex items-center gap-2 px-3 py-2 border border-blue-400/40 text-blue-400 text-xs font-bold uppercase tracking-widest hover:bg-blue-400/10 transition-colors disabled:opacity-50">
            {exporting === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            PDF
          </button>
          
          {/* Share Button */}
          <div className="relative">
            <button onClick={() => setShareMenu(!shareMenu)} data-testid="share-report-btn" 
              className="flex items-center gap-2 px-3 py-2 border border-white/20 text-white/60 text-xs font-bold uppercase tracking-widest hover:border-gold hover:text-gold transition-colors">
              <Share2 size={14} />
              Compartir
            </button>
            {shareMenu && (
              <div className="absolute right-0 top-full mt-2 z-20 bg-[#0A0A0A] border border-white/10 py-2 min-w-[160px]" data-testid="share-menu">
                <button onClick={() => shareVia('whatsapp')} data-testid="share-whatsapp" className="w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex items-center gap-3">
                  <MessageCircle size={14} className="text-emerald-400" /> WhatsApp
                </button>
                <button onClick={() => shareVia('email')} data-testid="share-email" className="w-full text-left px-4 py-2.5 text-xs hover:bg-white/5 flex items-center gap-3">
                  <Mail size={14} className="text-blue-400" /> Email
                </button>
              </div>
            )}
          </div>
          
          {/* AI Analysis */}
          <button onClick={runAI} disabled={aiLoading} data-testid="ai-analyze-btn" 
            className="flex items-center gap-2 px-4 py-2 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Brain size={14} />}
            Analisis IA
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-4">
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
          const count = groups[key]?.length || 0;
          const Icon = config.icon;
          return (
            <div key={key} className={`p-2 md:p-4 border ${config.bg} text-center`} data-testid={`stat-${key}`}>
              <Icon size={16} className={`${config.color} mx-auto mb-1 md:mb-2 md:w-5 md:h-5`} />
              <p className={`text-xl md:text-2xl font-bold font-heading ${config.color}`}>{count}</p>
              <p className="text-[8px] md:text-[10px] uppercase tracking-wider md:tracking-widest text-white/40 truncate">{config.shortLabel}</p>
            </div>
          );
        })}
      </div>

      {/* BCG Matrix Visual */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bcg-matrix">
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
          const items = groups[key] || [];
          const Icon = config.icon;
          return (
            <div key={key} className={`p-6 border ${config.bg} transition-all`} data-testid={`bcg-${key}`}>
              <div className="flex items-center gap-3 mb-4">
                <Icon size={20} className={config.color} />
                <div>
                  <h4 className={`text-lg font-semibold ${config.color}`}>{config.label}</h4>
                  <p className="text-[10px] text-white/40">{config.desc}</p>
                </div>
                <span className={`ml-auto text-2xl font-heading font-bold ${config.color}`}>{items.length}</span>
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-sm">{item.item_name}</span>
                    <span className="text-xs font-mono text-white/40">{item.food_cost_pct?.toFixed(1)}% FC</span>
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-white/20">Sin items</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Analysis */}
      {aiData && (
        <div className="bg-[#0A0A0A] border border-gold/20 p-6" data-testid="ai-analysis-result">
          <div className="flex items-center gap-2 mb-4">
            <Brain size={16} className="text-gold" />
            <h4 className="text-sm font-semibold text-gold uppercase tracking-widest">Analisis IA</h4>
          </div>
          <p className="text-sm text-white/60 mb-4">{aiData.summary}</p>
          {aiData.recommendations && (
            <div className="mb-4 p-4 bg-white/5 border border-white/10">
              <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Recomendaciones</p>
              <ul className="space-y-1">
                {aiData.recommendations.map((rec, i) => (
                  <li key={i} className="text-xs text-white/60 flex items-start gap-2">
                    <span className="text-gold mt-0.5">•</span> {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {aiData.classifications?.map((c, i) => (
            <div key={i} className="flex items-start justify-between py-2 border-b border-white/5">
              <div>
                <span className="text-sm font-medium">{c.item_name}</span>
                <span className={`ml-2 text-xs uppercase ${CATEGORY_CONFIG[c.category]?.color || 'text-white/40'}`}>{c.category}</span>
              </div>
              {c.suggestion && <p className="text-xs text-white/40 max-w-xs text-right">{c.suggestion}</p>}
            </div>
          ))}
        </div>
      )}

      {classifications.length === 0 && (
        <div className="text-center py-16 bg-[#0A0A0A] border border-white/5">
          <p className="text-white/30 text-sm mb-4">Agrega recetas con precio de venta para ver el analisis de Menu Engineering</p>
          <p className="text-xs text-white/20">La matriz BCG clasificara tus platos automaticamente</p>
        </div>
      )}
    </div>
  );
}
